import React, {useCallback, useEffect, useRef, useState} from "react";
import {format, parseISO} from "date-fns";
import {useTranslation} from "react-i18next";
import {ChevronDown} from "lucide-react";
import {CalendarPopover, DateCalendar, SegmentedField, calendarPopoverWidth} from "../../Common/DateInput";
import {useDateFnsLocale} from "../../../i18n/dateLocale";

/**
 * Parse a "YYYY-MM-DD" string as a LOCAL midnight Date,
 * avoiding the UTC-shift that `new Date("YYYY-MM-DD")` introduces.
 */
const parseLocalDate = (value) => {
    if (value instanceof Date) return value;
    // parseISO from date-fns always treats YYYY-MM-DD as local midnight
    return parseISO(typeof value === "string" ? value : String(value));
};

const toIso = (date) => format(date, "yyyy-MM-dd");

// Two months side by side when the viewport is wide enough, like before
const useMonthsShown = () => {
    const fits = () => (window.innerWidth >= calendarPopoverWidth(2) + 16 ? 2 : 1);
    const [months, setMonths] = useState(fits);
    useEffect(() => {
        const onResize = () => setMonths(fits());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    return months;
};

const DateRangePicker = ({dateRange, setDateRange, isDarkMode = false}) => {
    const {t} = useTranslation();
    const locale = useDateFnsLocale();
    const [showCalendar, setShowCalendar] = useState(false);
    // First grid click of a new range; the second click closes it
    const [anchor, setAnchor] = useState(null);
    const [hovered, setHovered] = useState(null);
    const triggerRef = useRef(null);
    const months = useMonthsShown();

    // Normalize incoming dates to avoid UTC-offset issues when the range was
    // restored from localStorage (where "new Date(isoString)" shifted to UTC).
    const start = parseLocalDate(dateRange[0].startDate);
    const end = parseLocalDate(dateRange[0].endDate);
    const startIso = toIso(start);
    const endIso = toIso(end);

    const commit = (fromIso, toIsoValue) => {
        const [a, b] = fromIso <= toIsoValue ? [fromIso, toIsoValue] : [toIsoValue, fromIso];
        setDateRange([{...dateRange[0], startDate: parseISO(a), endDate: parseISO(b), key: "selection"}]);
    };

    const close = useCallback(() => {
        setShowCalendar(false);
        setAnchor(null);
        setHovered(null);
    }, []);

    const handleGridPick = (iso) => {
        if (!anchor) {
            setAnchor(iso);
            commit(iso, iso);
        } else {
            commit(anchor, iso);
            close();
        }
    };

    // While picking the second end, preview the band up to the hovered day
    const previewRange = anchor
        ? (hovered && hovered < anchor ? [hovered, anchor] : [anchor, hovered || anchor])
        : [startIso, endIso];

    const fields = (
        <div className={`grid grid-cols-2 ${months > 1 ? "gap-8" : "gap-2"}`}>
            <div>
                <span className="block mb-1 text-xs text-gray-500 dark:text-gray-400">{t("datePicker.from", "Du")}</span>
                <SegmentedField compact autoFocus aria-label={t("datePicker.from", "Du")} value={startIso}
                                onEnter={close} onCommit={(iso) => { setAnchor(null); commit(iso, iso > endIso ? iso : endIso); }}/>
            </div>
            <div>
                <span className="block mb-1 text-xs text-gray-500 dark:text-gray-400">{t("datePicker.to", "Au")}</span>
                <SegmentedField compact aria-label={t("datePicker.to", "Au")} value={endIso}
                                onEnter={close} onCommit={(iso) => { setAnchor(null); commit(iso < startIso ? iso : startIso, iso); }}/>
            </div>
        </div>
    );

    return (
        <div className="relative text-center">
            <button
                ref={triggerRef}
                type="button"
                aria-haspopup="dialog"
                aria-expanded={showCalendar}
                className={`inline-flex items-center gap-2 pb-1 font-light text-xl ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}
                onClick={() => (showCalendar ? close() : setShowCalendar(true))}
            >
                {format(start, "d MMM yyyy", {locale})} <span className="mx-1">→</span> {format(end, "d MMM yyyy", {locale})}
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showCalendar ? "rotate-180" : ""}`}/>
            </button>

            {showCalendar && (
                <CalendarPopover anchorRef={triggerRef} onClose={close} align="center" width={calendarPopoverWidth(months)}>
                    <DateCalendar
                        value={anchor || startIso}
                        months={months}
                        range={previewRange}
                        field={fields}
                        onHover={anchor ? setHovered : undefined}
                        onSelect={(iso, {fromGrid}) => fromGrid && handleGridPick(iso)}
                    />
                </CalendarPopover>
            )}
        </div>
    );
};

export default DateRangePicker;
